import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, FieldArray } from 'formik';
import {
  DndContext,
  useDraggable,
  useDroppable,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';

// MUI
import {
  Box,
  Button,
  Card,
  Grid,
  IconButton,
  Paper,
  Typography,
  alpha,
  Tooltip,
  Skeleton,
  Alert,
  AlertTitle,
  Avatar,
  Chip,
} from '@mui/material';

// Icons
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import EditIcon from '@mui/icons-material/Edit';

// Custom
import { uploadImgStore } from '../../../../store/uploaders/thunks';
import { getImageStore } from '../../../../store/files/thunks';
import { validationSchema } from '../../../../components/formik/calificaciones/encuestas/formModels/validationSchema';
import { encuestaFormModel } from '../../../../components/formik/calificaciones/encuestas/formModels/encuestaFormModel';

import InputField from '../../../../components/formFields/InputField';
import TextAreaField from '../../../../components/formFields/TextAreaField';
import AutocompleteField from '../../../../components/formFields/autocompleteField';
import AutocompleteMultiple from '../../../../components/formFields/AutocompleteMultiple';
import SelectField from '../../../../components/formFields/selectField';
import CheckboxField from '../../../../components/formFields/checkBoxField';
import { UploadButtonField } from '../../../../components/formFields/uploadButtonField';
import { ModalDinamicTexts } from './ModalDinamicTexts';

const { formField } = encuestaFormModel;

export const SurveyImage = ({
  imageName,
  width = 80,
  height = 80,
  sx = {},
}: {
  imageName: string | null;
  width?: number | string;
  height?: number | string;
  sx?: any;
}) => {
  const dispatch = useDispatch();
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (imageName) {
      getImage();
    }
    return () => {
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    };
  }, [imageName]);

  const getImage = async () => {
    if (!imageName) return;
    setError(false);
    const response = await dispatch(getImageStore(imageName, 'surveys'));
    if (response instanceof Blob) {
      const url = URL.createObjectURL(response);
      setImageUrl(url);
    } else {
      setError(true);
    }
  };

  if (error)
    return (
      <Tooltip title="Error al cargar imagen">
        <BrokenImageIcon color="disabled" sx={{ fontSize: 24 }} />
      </Tooltip>
    );
  if (!imageUrl)
    return (
      <Skeleton variant="rectangular" width={width} height={height} sx={{ borderRadius: 1 }} />
    );

  return (
    <Tooltip title="Ver imagen">
      <Box
        component="img"
        src={imageUrl}
        sx={{
          width,
          height,
          borderRadius: 1,
          objectFit: 'contain',
          border: '1px solid #ddd',
          cursor: 'pointer',
          bgcolor: '#f5f5f5',
          ...sx,
        }}
        onClick={() => window.open(imageUrl, '_blank')}
      />
    </Tooltip>
  );
};

const METRIC_OPTIONS = [
  { id: 'NPS', label: 'NPS', description: '(Net Promoter Score)', color: '#5DCC0A' },
  { id: 'CSAT', label: 'CSAT', description: '(Customer satisfaction)', color: '#3f51b5' },
  { id: 'CES', label: 'CES', description: '(Customer Effort Score)', color: '#FFB400' },
];

const DraggableMetric = ({ metric, isUsed }: { metric: any; isUsed: boolean }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: metric.id,
    disabled: isUsed,
    data: { metric },
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : isUsed ? 0.4 : 1,
    cursor: isUsed ? 'not-allowed' : 'grab',
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      sx={{
        p: 1.5,
        borderRadius: 2,
        bgcolor: alpha(metric.color, 0.1),
        border: '1.5px dashed',
        borderColor: alpha(metric.color, 0.5),
        textAlign: 'center',
        minWidth: 140,
        transition: 'all 0.2s ease',
        '&:hover': {
          bgcolor: isUsed ? alpha(metric.color, 0.1) : alpha(metric.color, 0.2),
          transform: isUsed ? 'none' : 'translateY(-2px)',
          boxShadow: isUsed ? 'none' : `0 4px 12px ${alpha(metric.color, 0.2)}`,
        },
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: metric.color }}>
        {metric.label}
      </Typography>
      <Typography
        variant="caption"
        sx={{ fontSize: '0.65rem', color: 'text.secondary', fontWeight: 500 }}
      >
        {metric.description}
      </Typography>
    </Box>
  );
};

const MetricDropZone = ({
  index,
  currentMetric,
  onRemove,
}: {
  index: number;
  currentMetric: any;
  onRemove: () => void;
}) => {
  const { isOver, setNodeRef } = useDroppable({
    id: `dropzone-${index}`,
    data: { index },
  });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        minHeight: 80,
        width: '100%',
        borderRadius: 2,
        border: '2px dashed',
        borderColor: isOver
          ? 'primary.main'
          : currentMetric
            ? alpha(currentMetric.color, 0.3)
            : 'divider',
        bgcolor: isOver
          ? alpha('#3f51b5', 0.05)
          : currentMetric
            ? alpha(currentMetric.color, 0.02)
            : 'transparent',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.3s ease',
        position: 'relative',
        p: 1,
      }}
    >
      {currentMetric ? (
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: currentMetric.color }}>
            {currentMetric.label}
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontSize: '0.6rem', color: 'text.secondary', display: 'block' }}
          >
            {currentMetric.description}
          </Typography>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            sx={{ position: 'absolute', top: 2, right: 2, color: 'error.main' }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ) : (
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ fontStyle: 'italic', textAlign: 'center' }}
        >
          {isOver ? 'Suelta aquí' : 'Métrica (Opcional)'}
        </Typography>
      )}
    </Box>
  );
};

interface Props {
  selectedSurvey: any;
  editMode: boolean;
  handleSubmit: (values: any, formikHelpers: any) => void;
  handleBack: () => void;
  handleResponse: any;
  isViewMode?: boolean;
}

export const FormSurvey = ({
  selectedSurvey,
  editMode,
  isViewMode = false,
  handleSubmit,
  handleBack,
  handleResponse,
}: Props) => {
  const dispatch = useDispatch();
  const { areas_list_select, dinamic_texts_list, surveys_list } = useSelector(
    (state: any) => state.ratingsDevices
  );
  const review_links_options = surveys_list?.reviewLinks || [];
  const sensors = useSensors(useSensor(PointerSensor));

  const [openModalDinamicTexts, setOpenModalDinamicTexts] = useState(false);

  const handleImageUpload = async (event: any, index: number, setFieldValue: any, values: any) => {
    const file = event.target.files[0];
    if (!file) return;

    const localUrl = URL.createObjectURL(file);
    const oldImgName = (values.questions[index] as any).imgName;
    setFieldValue(`questions[${index}].localPreview`, localUrl);
    setFieldValue(`questions[${index}].imgName`, null);

    const codeFile = `survey_q`;
    const response = await dispatch(uploadImgStore(codeFile, file, () => {}));

    if (response.ok) {
      const imgName =
        response.data && typeof response.data === 'object' ? response.data.imgName : response.data;
      setFieldValue(`questions[${index}].imgName`, imgName);
    } else {
      handleResponse(response, 'subiendo imagen');
      setFieldValue(`questions[${index}].localPreview`, '');
      setFieldValue(`questions[${index}].imgName`, oldImgName);
      URL.revokeObjectURL(localUrl);
    }
  };

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        sx={{ mb: 2, textTransform: 'none' }}
      >
        Volver al listado
      </Button>

      <Formik
        initialValues={selectedSurvey}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, handleChange, handleBlur, errors, touched, setFieldValue }) => {
          const handleDragEnd = (event: any) => {
            const { active, over } = event;
            if (over && over.id.startsWith('dropzone-')) {
              const questionIndex = over.data.current.index;
              const metricId = active.id;

              const updatedQuestions = values.questions.map((q: any, idx: number) => {
                if (metricId === 'NPS' && q.metricType === 'NPS') return { ...q, metricType: null };
                if (idx === questionIndex) return { ...q, metricType: metricId };
                return q;
              });

              setFieldValue('questions', updatedQuestions);
            }
          };

          const usedMetrics = values.questions.map((q: any) => q.metricType).filter(Boolean);

          return (
            <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
              <Form>
                <fieldset disabled={isViewMode} style={{ border: 'none', padding: 0, margin: 0 }}>
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Card sx={{ p: 3, borderRadius: 2, boxShadow: 2 }}>
                        <Typography
                          variant="h6"
                          sx={{ mb: 2, color: 'primary.main', fontWeight: 700 }}
                        >
                          Información General
                        </Typography>
                        <InputField
                          fullWidth
                          autoComplete="off"
                          name={formField.name.name}
                          label={formField.name.label}
                          variant="outlined"
                          sx={{ mb: 3 }}
                        />
                        <Box sx={{ mb: 3 }}>
                          <AutocompleteField
                            required
                            name={formField.type.name}
                            label={formField.type.label}
                            options={[
                              { label: '+ Crear / Editar...', value: 'CREATE_EDIT' },
                              ...(dinamic_texts_list || [])
                                .filter((d: any) => d.status === 1 || d.status === undefined)
                                .map((d: any) => ({
                                  label: d.name || d.label,
                                  value: d.id || d.value,
                                })),
                            ]}
                            size="small"
                            onChange={(e: any, v: any) => {
                              if (v && v.value === 'CREATE_EDIT') {
                                setOpenModalDinamicTexts(true);
                                setTimeout(() => setFieldValue(formField.type.name, null), 0);
                              }
                            }}
                          />
                        </Box>
                        <Box sx={{ mb: 3 }}>
                          <AutocompleteField
                            required
                            name={formField.areas_rating_id.name}
                            label={formField.areas_rating_id.label}
                            options={areas_list_select || []}
                            size="small"
                          />
                        </Box>
                        <TextAreaField
                          fullWidth
                          name={formField.description.name}
                          label={formField.description.label}
                          variant="outlined"
                        />

                        <Box sx={{ mt: 3 }}>
                          {review_links_options.length === 0 ? (
                            <Alert severity="info" sx={{ mb: 2 }}>
                              <AlertTitle>Sin links de reseñas</AlertTitle>
                              No tienes links de reseñas sociales configurados. No es obligatorio,
                              pero si deseas que tus clientes dejen reseñas, créalos primero en la
                              sección de gestión de links.
                            </Alert>
                          ) : (
                            <AutocompleteMultiple
                              name={formField.reviewLinks.name}
                              label={formField.reviewLinks.label}
                              options={review_links_options.map((link: any) => ({
                                value: link.value || link.id,
                                label: link.name
                                  ? `${link.name} (${link.platform || link.label})`
                                  : link.platform || link.label || link.name,
                                icon: link.icon,
                              }))}
                              fullWidth
                              renderOption={(props: any, option: any) => (
                                <li {...props} key={option.value}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {option.icon ? (
                                      <Avatar
                                        src={`${window.location.origin}/social/${option.icon}`}
                                        alt={option.label}
                                        sx={{ width: 24, height: 24 }}
                                        variant="square"
                                      />
                                    ) : null}
                                    {option.label}
                                  </Box>
                                </li>
                              )}
                              renderTags={(value: any[], getTagProps: any) =>
                                value.map((option: any, index: number) => {
                                  const { key, ...tagProps } = getTagProps({ index });
                                  return (
                                    <Chip
                                      key={key}
                                      {...tagProps}
                                      label={option.label}
                                      avatar={
                                        option.icon ? (
                                          <Avatar
                                            src={`${window.location.origin}/social/${option.icon}`}
                                            alt={option.label}
                                            variant="square"
                                          />
                                        ) : undefined
                                      }
                                    />
                                  );
                                })
                              }
                            />
                          )}
                        </Box>
                      </Card>

                      {!isViewMode && (
                        <Box sx={{ position: 'sticky', top: 20, zIndex: 10, mt: 3 }}>
                          <Card
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              boxShadow: 4,
                              border: '1px solid',
                              borderColor: 'primary.main',
                              bgcolor: alpha('#3f51b5', 0.02),
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{
                                mb: 1.5,
                                fontWeight: 700,
                                color: 'primary.main',
                                textAlign: 'center',
                                textTransform: 'uppercase',
                                letterSpacing: 1,
                              }}
                            >
                              Métricas Disponibles
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                gap: 2,
                                justifyContent: 'center',
                                flexWrap: 'wrap',
                              }}
                            >
                              {METRIC_OPTIONS.map((metric) => {
                                const isExclusive = metric.id === 'NPS';
                                const isAlreadyUsed = usedMetrics.includes(metric.id);
                                return (
                                  <DraggableMetric
                                    key={metric.id}
                                    metric={metric}
                                    isUsed={isExclusive && isAlreadyUsed}
                                  />
                                );
                              })}
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{
                                mt: 2,
                                display: 'block',
                                textAlign: 'center',
                                color: 'text.secondary',
                                fontStyle: 'italic',
                              }}
                            >
                              Arrastra una métrica a una pregunta para asignarla.
                            </Typography>
                          </Card>
                        </Box>
                      )}
                    </Grid>

                    <Grid size={{ xs: 12, md: 8 }}>
                      <Card sx={{ p: 3, borderRadius: 2, boxShadow: 2 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                          }}
                        >
                          <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700 }}>
                            Preguntas de la Encuesta
                          </Typography>
                        </Box>

                        <FieldArray name="questions">
                          {({ push, remove, swap }) => (
                            <Box>
                              {values.questions.map((question: any, index: number) => (
                                <Paper
                                  key={index}
                                  elevation={0}
                                  sx={{
                                    p: 2,
                                    mb: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    backgroundColor: alpha('#663cc1', 0.02),
                                  }}
                                >
                                  <Grid container spacing={3}>
                                    <Grid size={{ xs: 12, sm: 10 }}>
                                      <InputField
                                        fullWidth
                                        label={`${formField.questionText.label} #${index + 1}`}
                                        name={`${formField.questions.name}[${index}].${formField.questionText.name}`}
                                        size="small"
                                        multiline
                                      />
                                    </Grid>
                                    <Grid
                                      size={{ xs: 12, sm: 2 }}
                                      sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        alignItems: 'center',
                                        gap: 0.5,
                                      }}
                                    >
                                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <IconButton
                                          size="small"
                                          disabled={index === 0}
                                          onClick={() => swap(index, index - 1)}
                                        >
                                          <ArrowUpwardIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          disabled={index === values.questions.length - 1}
                                          onClick={() => swap(index, index + 1)}
                                        >
                                          <ArrowDownwardIcon fontSize="small" />
                                        </IconButton>
                                      </Box>
                                      <IconButton
                                        color="error"
                                        onClick={() => {
                                          if (editMode) {
                                            const confirmed = window.confirm(
                                              '¿Está seguro de que desea eliminar esta pregunta?'
                                            );
                                            if (!confirmed) return;
                                          }
                                          remove(index);
                                        }}
                                        disabled={values.questions.length === 1}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Grid>

                                    <Grid size={{ xs: 12, sm: 4 }}>
                                      <MetricDropZone
                                        index={index}
                                        currentMetric={METRIC_OPTIONS.find(
                                          (m) => m.id === question.metricType
                                        )}
                                        onRemove={() =>
                                          setFieldValue(`questions[${index}].metricType`, null)
                                        }
                                      />
                                    </Grid>

                                    <Grid size={{ xs: 12, sm: 4 }}>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 2,
                                          p: 1,
                                          border: '1px solid',
                                          borderColor: 'divider',
                                          borderRadius: 2,
                                          height: '100%',
                                          minHeight: 80,
                                          justifyContent: 'center',
                                        }}
                                      >
                                        {!question.imgName && !question.localPreview ? (
                                          <UploadButtonField
                                            text="Subir Imagen"
                                            name={`questions[${index}].imageFile`}
                                            accept="image/*"
                                            onChange={(e: any) =>
                                              handleImageUpload(e, index, setFieldValue, values)
                                            }
                                            style={{ textTransform: 'none', fontSize: '0.8rem' }}
                                          />
                                        ) : (
                                          <Box
                                            sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                                          >
                                            {question.localPreview ? (
                                              <Tooltip title="Vista previa">
                                                <Box
                                                  component="img"
                                                  src={question.localPreview}
                                                  sx={{
                                                    width: 60,
                                                    height: 60,
                                                    borderRadius: 1,
                                                    objectFit: 'contain',
                                                    border: '1px solid #ddd',
                                                    cursor: 'pointer',
                                                    bgcolor: '#f5f5f5',
                                                  }}
                                                  onClick={() =>
                                                    window.open(question.localPreview, '_blank')
                                                  }
                                                />
                                              </Tooltip>
                                            ) : (
                                              <SurveyImage
                                                imageName={question.imgName}
                                                width={60}
                                                height={60}
                                              />
                                            )}
                                            <Box
                                              sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 0.5,
                                              }}
                                            >
                                              <Tooltip title="Cambiar imagen">
                                                <Box>
                                                  <UploadButtonField
                                                    text=""
                                                    icon={<EditIcon fontSize="small" />}
                                                    name={`questions[${index}].imageFile`}
                                                    accept="image/*"
                                                    onChange={(e: any) =>
                                                      handleImageUpload(
                                                        e,
                                                        index,
                                                        setFieldValue,
                                                        values
                                                      )
                                                    }
                                                    style={{
                                                      minWidth: 0,
                                                      padding: '4px',
                                                      borderRadius: '50%',
                                                    }}
                                                  />
                                                </Box>
                                              </Tooltip>
                                              <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => {
                                                  if (question.localPreview)
                                                    URL.revokeObjectURL(question.localPreview);
                                                  setFieldValue(
                                                    `questions[${index}].imgName`,
                                                    null
                                                  );
                                                  setFieldValue(
                                                    `questions[${index}].localPreview`,
                                                    ''
                                                  );
                                                }}
                                              >
                                                <DeleteIcon fontSize="small" />
                                              </IconButton>
                                            </Box>
                                          </Box>
                                        )}
                                      </Box>
                                    </Grid>

                                    <Grid
                                      size={{ xs: 12, sm: 4 }}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          p: 1.5,
                                          border: '1px solid',
                                          borderColor: alpha('#3f51b5', 0.1),
                                          borderRadius: 2,
                                          bgcolor: alpha('#3f51b5', 0.02),
                                          width: '100%',
                                          height: '100%',
                                          minHeight: 80,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                        }}
                                      >
                                        <CheckboxField
                                          name={`${formField.questions.name}[${index}].${formField.isRequired.name}`}
                                          label={formField.isRequired.label}
                                        />
                                      </Box>
                                    </Grid>

                                    <Grid
                                      size={{ xs: 12, sm: 6 }}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          p: 1.5,
                                          border: '1px solid',
                                          borderColor: alpha('#3f51b5', 0.1),
                                          borderRadius: 2,
                                          bgcolor: alpha('#3f51b5', 0.02),
                                          width: '100%',
                                          height: '100%',
                                          minHeight: 80,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                        }}
                                      >
                                        <Tooltip
                                          title="Habilita un cuadro de texto para que la persona responda con texto o envíe una nota de voz"
                                          arrow
                                          placement="top"
                                        >
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              width: '100%',
                                              justifyContent: 'center',
                                            }}
                                          >
                                            <CheckboxField
                                              name={`${formField.questions.name}[${index}].${formField.isOpenQuestion.name}`}
                                              label={formField.isOpenQuestion.label}
                                            />
                                          </Box>
                                        </Tooltip>
                                      </Box>
                                    </Grid>

                                    <Grid
                                      size={{ xs: 12, sm: 6 }}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          p: 1.5,
                                          border: '1px solid',
                                          borderColor: alpha('#3f51b5', 0.1),
                                          borderRadius: 2,
                                          bgcolor: alpha('#3f51b5', 0.02),
                                          width: '100%',
                                          height: '100%',
                                          minHeight: 80,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                        }}
                                      >
                                        {question.isOpenQuestion ? (
                                          <SelectField
                                            fullWidth
                                            name={`${formField.questions.name}[${index}].${formField.openQuestionType.name}`}
                                            label={formField.openQuestionType.label}
                                            data={[
                                              { value: 'text', label: 'Texto' },
                                              { value: 'voice', label: 'Nota de Voz' },
                                              { value: 'both', label: 'Ambos (Texto y Voz)' },
                                            ]}
                                            size="small"
                                          />
                                        ) : (
                                          <Typography
                                            variant="body2"
                                            color="text.disabled"
                                            sx={{ fontStyle: 'italic' }}
                                          >
                                            Formato de respuesta (No requerido)
                                          </Typography>
                                        )}
                                      </Box>
                                    </Grid>
                                  </Grid>
                                </Paper>
                              ))}
                              {!isViewMode && (
                                <Button
                                  variant="outlined"
                                  startIcon={<AddIcon />}
                                  onClick={() =>
                                    push({
                                      questionText: '',
                                      order: values.questions.length,
                                      isRequired: false,
                                      isOpenQuestion: false,
                                      openQuestionType: 'text',
                                      imgName: null,
                                      localPreview: '',
                                      metricType: null,
                                    })
                                  }
                                  sx={{ mt: 1, textTransform: 'none' }}
                                >
                                  Añadir Pregunta
                                </Button>
                              )}
                            </Box>
                          )}
                        </FieldArray>
                      </Card>

                      {!isViewMode && (
                        <Box sx={{ display: 'flex', justifyContent: 'end', mt: 3 }}>
                          <Button
                            type="submit"
                            variant="contained"
                            startIcon={<SaveIcon />}
                            sx={{ px: 4, py: 1.5, borderRadius: 2, fontWeight: 700 }}
                          >
                            {editMode ? 'Guardar Cambios' : 'Guardar Encuesta'}
                          </Button>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </fieldset>
              </Form>
            </DndContext>
          );
        }}
      </Formik>

      <ModalDinamicTexts
        open={openModalDinamicTexts}
        onClose={() => setOpenModalDinamicTexts(false)}
      />
    </Box>
  );
};
